import { NavigationCard, NavigationCardTypes, HStack } from '@genial/components';
import { Constant } from '@genial/config';
import { Spacing } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import { Utils } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { useFlag } from '@unleash/proxy-client-react';
import { Box } from 'native-base';
import React from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import { searchTermState } from '../../../../store/blockList/atom';
import { useCustomerActions } from '../../../../store/customer/actions';
import { listCustomersBySearchTermState } from '../../../../store/customer/atom';
import { useOrderActions } from '../../../../store/order/actions';
import { useProductActions } from '../../../../store/product/actions';
import { sessionState } from '../../../../store/session/atom';
import { SectionInnerCard } from '../SectionInnerCard';
import { SectionOuterCard } from '../SectionOuterCard';
import { SectionTitle } from '../SectionTitle';

interface NavigationOptionProps extends NavigationCardTypes.Props {
  mobileOrder: number;
}

interface InvestmentOrdersProps {
  isMobile?: boolean;
  isTablet?: boolean;
}

export const InvestmentOrders: React.FC<InvestmentOrdersProps> = ({ isMobile }) => {
  const { userProfile } = useRecoilValue(sessionState);

  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const blockListIsVisible = useFlag(Constant.UnleashToggleBlockList);
  const batchTicketIsVisible = useFlag(Constant.UnleashToggleBatchTicket);
  const cartVisible = userProfile?.isCarrinho;
  const resetListBlockListBySearchTerm = useResetRecoilState(searchTermState);
  const resetListCustomersBySearchTerm = useResetRecoilState(listCustomersBySearchTermState);

  const { resetProductFilterAndSearchParams, resetProduct } = useProductActions();
  const { resetCustomer, resetCustomerAndSearchTermParams } = useCustomerActions();
  const { resetOrder } = useOrderActions();

  const onFastTicketPress = () => {
    resetProductFilterAndSearchParams();
    resetCustomer();
    resetProduct();
    resetOrder();
    resetCustomerAndSearchTermParams();
    resetListCustomersBySearchTerm();

    navigate('NewApplication');
  };
  const onTicketInBatchPress = () => navigate('BatchTicketRequests');
  const onMyOrdersPress = () => navigate('OrdersHistory');
  const onBlockListPress = () => {
    resetListBlockListBySearchTerm();
    navigate('BlockList');
  };
  const onCartPress = () => {
    navigate({
      name: 'AngularFrame_ShoppingCart',
      params: { state: 'app.carrinhoDeCompras.inicio' },
    });
  };

  const blockListOption: NavigationOptionProps[] = [
    {
      title: 'Clientes restritos',
      description:
        'Solicite o desbloqueio de clientes que optaram por não receber ordens via WhatsApp.',
      buttonText: 'Acessar ',
      icon: 'Lock' as IconographyTypes.Option,
      testId: 'block-list',
      buttonOnPress: onBlockListPress,
      mobileOrientation: 'vertical',
      mobileOrder: 5,
    },
  ];

  const cartOption: NavigationOptionProps[] = [
    {
      title: 'Carrinho de Compras',
      description:
        'Use o carrinho de compras para enviar ordens de investimento para um ou mais clientes.',
      buttonText: 'Acessar ',
      icon: 'ShoppingCart' as IconographyTypes.Option,
      testId: 'cart',
      buttonOnPress: onCartPress,
      mobileOrientation: 'vertical',
      mobileOrder: 4,
    },
  ];

  const navigationOptions: NavigationOptionProps[] = [
    {
      title: 'Boleta Fácil',
      description:
        'Envie uma oportunidade de investimento para seus clientes rapidamente e com poucos cliques.',
      buttonText: 'Acessar ',
      icon: 'Investiment',
      testId: 'fast-ticket',
      buttonOnPress: onFastTicketPress,
      mobileOrientation: 'horizontal',
      mobileOrder: 1,
    },
    {
      title: 'Boleta em lote',
      description:
        'Envie oportunidades de investimento para um ou mais clientes de forma prática e eficiente.',
      buttonText: 'Acessar ',
      icon: 'BusinessAndFinance',
      testId: 'ticket-in-batch',
      buttonOnPress: onTicketInBatchPress,
      disabled: !batchTicketIsVisible,
      mobileOrientation: 'vertical',
      mobileOrder: 2,
    },
    {
      title: 'Minhas Ordens',
      description:
        'Tenha uma visão clara de todas as suas ordens em um único lugar, facilitando a organização.',
      buttonText: 'Acessar ',
      icon: 'File',
      testId: 'my-orders',
      buttonOnPress: onMyOrdersPress,
      mobileOrientation: 'vertical',
      mobileOrder: 3,
    },
    ...(cartVisible ? cartOption : []),
    ...(blockListIsVisible ? blockListOption : []),
  ];

  const renderNavigationOptions = () => {
    const options = [...navigationOptions];
    const sortedNavigationOptions = options.sort((a, b) => a.mobileOrder - b.mobileOrder);
    if (isMobile) {
      return (
        <Box
          flexWrap="wrap"
          flexDirection="row"
          justifyContent="space-between"
          style={{ gap: Utils.Token.ToInt(Spacing, 'inline.xxxs') }}>
          {sortedNavigationOptions.map((navigationOption) => (
            <NavigationCard key={navigationOption.title} {...navigationOption} isMobile />
          ))}
        </Box>
      );
    }

    return (
      <HStack space={Spacing['stack.nano']}>
        {navigationOptions.map((navigationOption) => (
          <NavigationCard key={navigationOption.title} isCompact {...navigationOption} />
        ))}
      </HStack>
    );
  };

  return (
    <SectionOuterCard>
      <SectionTitle title="Ordens de investimento" description="Escolha a opção que deseja usar:" />
      <SectionInnerCard>{renderNavigationOptions()}</SectionInnerCard>
    </SectionOuterCard>
  );
};
